import apiClient from './api'

export interface CrudField {
  id?: string
  name: string
  type: 'string' | 'number' | 'boolean' | 'date' | 'datetime' | 'text' | 'select' | 'decimal'
  required: boolean
  max_length?: number
  default_value?: string
  is_primary_key: boolean
  is_searchable: boolean
  show_in_list: boolean
  show_in_form?: boolean
  select_options?: string[]
  // Para DECIMAL
  precision?: number
  scale?: number
}

export interface CrudPermissions {
  read: boolean
  create: boolean
  update: boolean
  delete: boolean
}

export interface CreateCrudData {
  name: string
  table_name: string
  description?: string
  nombre_menu?: string
  fields: CrudField[]
  has_api?: boolean
  has_frontend?: boolean
  permissions?: CrudPermissions
}

export interface CrudEntity extends Required<CreateCrudData> {
  id: number
  is_active: boolean
  created_at: string
  updated_at: string
  display_fields: string[]
  editable_fields: string[]
  searchable_fields: string[]
  sortable_fields: string[]
}

class CrudService {
  private baseUrl = 'crud-configs'

  private normalizeEntity(raw: any): CrudEntity {
    let fields: any = raw.fields
    if (typeof fields === 'string') {
      try { fields = JSON.parse(fields) } catch { fields = [] }
    }
    // Normalizar tipos obsoletos: mapear 'email' -> 'string' para evitar inconsistencias
    if (Array.isArray(fields)) {
      fields = fields.map((f: any, index: number) => {
        if (!f || typeof f !== 'object') return f
        
        // Generar ID único para campos que no lo tienen (basado solo en posición)
        const fieldWithId = {
          ...f,
          id: f.id || `field-${index}`
        }
        
        // email -> string (legacy)
        if (fieldWithId.type === 'email') return { ...fieldWithId, type: 'string' }
        // Defaults para DECIMAL
        if (fieldWithId.type === 'decimal') {
          const result = { ...fieldWithId, type: 'string' };
          if (fieldWithId.precision === undefined) result.precision = 18;
          if (fieldWithId.scale === undefined) result.scale = 2;
          return result;
        }
        return fieldWithId
      })
    }
    let permissions: any = raw.permissions
    if (typeof permissions === 'string') {
      try { permissions = JSON.parse(permissions) } catch { permissions = { read: true, create: true, update: true, delete: true } }
    }
    return {
      id: Number(raw.id),
      name: raw.name,
      table_name: raw.table_name,
      description: raw.description ?? '',
      nombre_menu: raw.nombre_menu ?? raw.menuLabel ?? raw.displayName ?? raw.name,
      fields,
      display_fields: raw.display_fields || [],
      editable_fields: raw.editable_fields || [],
      searchable_fields: raw.searchable_fields || [],
      sortable_fields: raw.sortable_fields || [],
      has_api: Boolean(raw.has_api ?? true),
      has_frontend: Boolean(raw.has_frontend ?? true),
      permissions,
      is_active: Boolean(raw.is_active ?? true),
      created_at: raw.created_at || raw.createdAt || '',
      updated_at: raw.updated_at || raw.updatedAt || '',
    }
  }

  async getCruds(): Promise<CrudEntity[]> {
    const res = await apiClient.get(this.baseUrl)
    const list = res.data.data || []
    return list.map((item: any) => this.normalizeEntity(item))
  }

  async getCrud(id: number | string): Promise<CrudEntity> {
    const res = await apiClient.get(`${this.baseUrl}/${id}`)
    return this.normalizeEntity(res.data.data)
  }

  async createCrud(data: CreateCrudData): Promise<CrudEntity> {
    // El backend espera camelCase/estructura según DTO, pero usa keys como table_name y fields JSON-inlined
    const payload: any = {
      name: data.name,
      table_name: data.table_name,
      description: data.description,
      nombre_menu: data.nombre_menu,
      fields: data.fields.map(f => ({
        name: f.name,
        type: f.type,
        required: f.required,
        max_length: f.max_length,
        default_value: f.default_value,
        is_primary_key: f.is_primary_key,
        is_searchable: f.is_searchable,
        show_in_list: f.show_in_list,
        show_in_form: f.show_in_form,
        select_options: f.select_options,
        precision: f.type === 'decimal' ? (f.precision ?? 18) : undefined,
        scale: f.type === 'decimal' ? (f.scale ?? 2) : undefined,
      })),
      has_api: data.has_api ?? true,
      has_frontend: data.has_frontend ?? true,
      permissions: data.permissions ?? { read: true, create: true, update: true, delete: true },
    }

    const res = await apiClient.post(this.baseUrl, payload)
    return this.normalizeEntity(res.data.data)
  }

  async updateCrud(id: number | string, data: Partial<CreateCrudData>): Promise<CrudEntity> {
    // Función mejorada para eliminar recursivamente la propiedad 'property' de cualquier objeto
    const removePropertyRecursively = (obj: any): any => {
      if (!obj || typeof obj !== 'object') return obj;
      
      // Si es un array, aplicar a cada elemento
      if (Array.isArray(obj)) {
        return obj.map(item => removePropertyRecursively(item));
      }
      
      // Si es un objeto, crear una copia limpia sin la propiedad 'property'
      const result: any = {};
      
      // Copiar solo las propiedades que no son 'property'
      for (const key in obj) {
        if (key !== 'property') {
          // Aplicar recursivamente a valores que son objetos
          if (obj[key] && typeof obj[key] === 'object') {
            result[key] = removePropertyRecursively(obj[key]);
          } else {
            result[key] = obj[key];
          }
        }
      }
      
      return result;
    };
    
    // Limpiar todo el objeto de datos
    const cleanData = removePropertyRecursively(data);
    
    // Crear un payload simplificado sin propiedades problemáticas
    const payload: any = {
      name: cleanData.name,
      table_name: cleanData.table_name,
      description: cleanData.description,
      nombre_menu: cleanData.nombre_menu,
      has_api: cleanData.has_api,
      has_frontend: cleanData.has_frontend,
      permissions: cleanData.permissions
    }
    
    // Filtrar propiedades undefined
    Object.keys(payload).forEach(key => {
      if (payload[key] === undefined) delete payload[key]
    })
    
    // Manejar los campos de forma especial
    if (cleanData.fields && cleanData.fields.length > 0) {
      // Convertir los campos a objetos simples con solo las propiedades permitidas
      payload.fields = cleanData.fields.map((f: any) => {
        // Crear un objeto nuevo con solo las propiedades permitidas
        return {
          name: f.name,
          type: f.type,
          required: f.required || false,
          is_primary_key: f.is_primary_key || false,
          is_searchable: f.is_searchable || false,
          show_in_list: f.show_in_list || false,
          show_in_form: f.show_in_form !== undefined ? !!f.show_in_form : true,
          max_length: f.max_length,
          default_value: f.default_value,
          select_options: f.select_options,
          // Incluir ID siempre (necesario para detectar renombrados)
          id: f.id,
          // Solo incluir precision y scale para campos decimales
          ...(f.type === 'decimal' ? {
            precision: f.precision ?? 18,
            scale: f.scale ?? 2
          } : {})
        }
      })
      
      // campos listos para enviar
    }
    if (data.has_api !== undefined) payload.has_api = data.has_api
    if (data.has_frontend !== undefined) payload.has_frontend = data.has_frontend
    if (data.permissions !== undefined) payload.permissions = data.permissions

    const res = await apiClient.put(`${this.baseUrl}/${id}`, payload)
    return this.normalizeEntity(res.data.data)
  }

  async deleteCrud(id: number | string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`)
  }

  async exportData(crudId: number, params?: { search?: string; filters?: Record<string, any> }): Promise<string> {
    const response = await apiClient.get(`${this.baseUrl}/${crudId}/data/export`, { 
      params,
      responseType: 'text'
    })
    return response.data
  }

  async generateCrud(id: number | string): Promise<{ id: number; status: string }> {
    const res = await apiClient.post(`${this.baseUrl}/${id}/generate`)
    return res.data.data
  }

  async cleanupArtifacts(id: number | string): Promise<{ success: boolean; id: number; message?: string }> {
    const res = await apiClient.post(`${this.baseUrl}/${id}/cleanup`)
    // Algunos endpoints devuelven envueltos en { data }, otros no. Soportar ambos.
    return res.data?.data ?? res.data
  }

  async listData(
    crudId: number | string,
    params: { page?: number; pageSize?: number; search?: string; sortBy?: string; sortDir?: 'asc' | 'desc'; filters?: Record<string, any> } = {}
  ): Promise<{ items: Record<string, any>[]; total: number; page: number; pageSize: number }> {
    const res = await apiClient.get(`crud-configs/${crudId}/data`, {
      params: {
        ...params,
        limit: params.pageSize, // Mapear pageSize a limit para el backend
        filters: params.filters ? JSON.stringify(params.filters) : undefined,
      },
    })
    return res.data.data
  }

  async getData(crudId: number | string, pk: string | number): Promise<any> {
    const res = await apiClient.get(`crud-configs/${crudId}/data/${pk}`)
    return res.data.data
  }

  async createData(crudId: number | string, values: Record<string, any>): Promise<any> {
    const res = await apiClient.post(`crud-configs/${crudId}/data`, values)
    return res.data.data
  }

  async updateData(crudId: number | string, pk: string | number, values: Record<string, any>): Promise<any> {
    const res = await apiClient.put(`crud-configs/${crudId}/data/${pk}`, values)
    return res.data.data
  }

  async deleteData(crudId: number | string, pk: string | number): Promise<any> {
    const res = await apiClient.delete(`crud-configs/${crudId}/data/${pk}`)
    return res.data.data
  }

async exportCsv(
  crudId: number | string,
  params: { search?: string; filters?: Record<string, any>; allFields?: boolean; page?: number; pageSize?: number; sortBy?: string; sortDir?: 'asc' | 'desc' } = {}
): Promise<string> {
    const res = await apiClient.get(`crud-configs/${crudId}/data/export`, {
      params: {
        search: params.search,
        filters: params.filters ? JSON.stringify(params.filters) : undefined,
        allFields: params.allFields ? 'true' : undefined,
        page: params.page,
        limit: params.pageSize,
        sortBy: params.sortBy,
        sortOrder: params.sortDir,
      },
    })
    return res.data.data?.csv || res.data || ''
  }

  async getRelationsByCrudId(crudId: number | string): Promise<Array<{ id: number; crudId: number; fieldName: string; relatedTable: string; displayField: string; isActive: boolean }>> {
    const res = await apiClient.get(`crud-relations/crud/${crudId}`)
    // Algunos endpoints pueden devolver envueltos en { data }
    return res.data?.data ?? res.data
  }

  async createRelation(data: {
    crudId: number
    fieldName: string
    relatedTable: string
    displayField: string
    isActive: boolean
  }): Promise<any> {
    const res = await apiClient.post('crud-relations', data)
    return res.data.data
  }

  async updateRelation(id: number, data: {
    relatedTable?: string
    displayField?: string
    isActive?: boolean
  }): Promise<any> {
    const res = await apiClient.put(`crud-relations/${id}`, data)
    return res.data?.data ?? res.data
  }

  async deleteRelation(id: number): Promise<any> {
    const res = await apiClient.delete(`crud-relations/${id}`)
    return res.data?.data ?? res.data
  }

  async detectRelationFields(crudId: number | string): Promise<string[]> {
    const res = await apiClient.get(`crud-relations/crud/${crudId}/detect-fields`)
    return res.data?.data ?? res.data ?? []
  }

  async getAvailableTables(): Promise<string[]> {
    const res = await apiClient.get('crud-relations/tables')
    return res.data?.data ?? res.data ?? []
  }

  async getTableColumns(tableName: string): Promise<Array<{ name: string; data_type?: string }>> {
    const res = await apiClient.get(`crud-relations/tables/${tableName}/columns`)
    return res.data?.data ?? res.data ?? []
  }
}

export const crudService = new CrudService()
export default crudService
